import {
  PlanTemplate,
  TemplateQuestion,
  TemplateSection,
} from "../../Store/Reducers/AccountPlanTemplateSlice";

export type ChangeKind = "add" | "edit" | "del" | "order";
export type Kind = "questions" | "sections";

export interface TemplateChange {
  kind: ChangeKind;
  what: "question" | "section";
  label: string;
}

type Item = TemplateQuestion | TemplateSection;

// Key order is not meaningful: the server stores templates as JSONB, which reorders keys.
const stable = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    return `{${Object.keys(obj)
      .sort()
      .map(k => `${JSON.stringify(k)}:${stable(obj[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

export const same = (a: unknown, b: unknown): boolean =>
  stable(a) === stable(b);

export const itemLabel = (item: Item): string =>
  "text" in item ? item.text : item.title;

function compare(
  what: "question" | "section",
  live: Item[],
  draft: Item[]
): TemplateChange[] {
  const out: TemplateChange[] = [];
  const liveByKey = new Map(live.map(x => [x.key, x]));
  const draftByKey = new Map(draft.map(x => [x.key, x]));
  draft.forEach(x => {
    const before = liveByKey.get(x.key);
    if (!before) out.push({ kind: "add", what, label: itemLabel(x) });
    else if (!same(before, x))
      out.push({ kind: "edit", what, label: itemLabel(x) });
  });
  live.forEach(x => {
    if (!draftByKey.has(x.key))
      out.push({ kind: "del", what, label: itemLabel(x) });
  });
  const liveOrder = live.map(x => x.key).filter(k => draftByKey.has(k));
  const draftOrder = draft.map(x => x.key).filter(k => liveByKey.has(k));
  if (!same(liveOrder, draftOrder))
    out.push({ kind: "order", what, label: "" });
  return out;
}

/** What publishing `draft` would change, compared with what is live. */
export function templateChanges(
  live: PlanTemplate,
  draft: PlanTemplate | undefined
): TemplateChange[] {
  if (!draft) return [];
  return [
    ...compare("question", live.questions, draft.questions),
    ...compare("section", live.sections, draft.sections),
  ];
}

export type Origin = "default" | "customised" | "added" | null;

/** Where an item in a tenant's template comes from, relative to the ExecBridge default. */
export function itemOrigin(
  isDefaultScope: boolean,
  defaults: PlanTemplate,
  kind: Kind,
  item: Item
): Origin {
  if (isDefaultScope) return null;
  const base = (defaults[kind] as Item[]).find(x => x.key === item.key);
  if (!base) return "added";
  return same(base, item) ? "default" : "customised";
}

/** Whether a tenant's item differs from the default copy it started from. */
export const isCustomisedFromDefault = (
  defaults: PlanTemplate,
  kind: Kind,
  item: Item
): boolean => {
  const base = (defaults[kind] as Item[]).find(x => x.key === item.key);
  return !!base && !same(base, item);
};
